export const ROLES = {
  ADMIN: 'admin',
  AUDITOR: 'auditor',
  AUDITEE: 'auditee',
  CUSTOMER_LEGACY: 'customer',
  ANALYST: 'analyst',
};

export const ROLE_ALIASES = {
  [ROLES.CUSTOMER_LEGACY]: ROLES.AUDITEE,
};

export const AUDITEE_ROLES = [ROLES.CUSTOMER_LEGACY, ROLES.AUDITEE];

export const ROLE_GROUPS = {
  admins: [ROLES.ADMIN],
  auditStaff: [ROLES.ADMIN, ROLES.AUDITOR],
  auditees: [ROLES.AUDITEE],
  auditParticipants: [ROLES.ADMIN, ROLES.AUDITOR, ROLES.AUDITEE],
  readOnlyBusiness: [ROLES.ANALYST],
};

export const ACCESS_MATRIX = {
  dashboard: { view: ROLE_GROUPS.auditParticipants },
  search: { view: ROLE_GROUPS.auditParticipants },
  audits: {
    view: ROLE_GROUPS.auditParticipants,
    create: ROLE_GROUPS.auditStaff,
    edit: ROLE_GROUPS.auditStaff,
    answer: ROLE_GROUPS.auditParticipants,
    assign: ROLE_GROUPS.auditStaff,
    close: ROLE_GROUPS.auditStaff,
  },
  issues: { view: ROLE_GROUPS.auditParticipants, create: ROLE_GROUPS.auditParticipants, edit: ROLE_GROUPS.auditStaff },
  corrective_actions: { view: ROLE_GROUPS.auditParticipants, edit: ROLE_GROUPS.auditStaff },
  notifications: { view: [ROLES.ADMIN, ROLES.AUDITOR, ROLES.AUDITEE, ROLES.ANALYST] },
  self_check: { view: ROLE_GROUPS.auditees, edit: ROLE_GROUPS.auditees },
  templates: { view: ROLE_GROUPS.auditStaff, edit: ROLE_GROUPS.auditStaff },
  reference_data: { view: ROLE_GROUPS.auditStaff, edit: ROLE_GROUPS.auditStaff },
  management: { view: ROLE_GROUPS.auditStaff },
  admin: { view: ROLE_GROUPS.admins, edit: ROLE_GROUPS.admins },
  company_members: { view: ROLE_GROUPS.auditParticipants, edit: ROLE_GROUPS.auditParticipants },
  email_queue: { view: ROLE_GROUPS.admins, edit: ROLE_GROUPS.admins },
  procedure: { view: ROLE_GROUPS.auditStaff, edit: ROLE_GROUPS.auditStaff },
  auditor_pool: { view: ROLE_GROUPS.auditStaff, edit: ROLE_GROUPS.auditStaff },
  analytics: {
    view: [ROLES.ADMIN, ROLES.AUDITOR, ROLES.ANALYST, ROLES.AUDITEE],
    edit: [ROLES.ADMIN, ROLES.AUDITOR, ROLES.ANALYST],
  },
  executive_dashboard: { view: [ROLES.ADMIN, ROLES.ANALYST] },
  erp_flags: { view: [ROLES.ADMIN, ROLES.AUDITOR, ROLES.ANALYST], edit: [ROLES.ADMIN, ROLES.AUDITOR] },
};

export function getRole(value) {
  return typeof value === 'string' ? value : value?.role;
}

export function canonicalRole(value) {
  const role = getRole(value);
  return ROLE_ALIASES[role] || role;
}

export function isAuditeeRole(value) {
  return canonicalRole(value) === ROLES.AUDITEE;
}

export function canAnswerAuditQuestion(value, answerScope = 'auditor', enabled = true) {
  if (!enabled) return false;

  const role = canonicalRole(value);
  const scope = ['auditor', 'customer', 'both'].includes(answerScope)
    ? answerScope
    : 'auditor';

  if (role === ROLES.ADMIN) return true;
  if (role === ROLES.AUDITOR) return scope === 'auditor' || scope === 'both';
  if (role === ROLES.AUDITEE) return scope === 'customer' || scope === 'both';
  return false;
}

export function hasRole(user, roles = []) {
  if (!roles || roles.length === 0) return true;
  if (!user) return false;
  const userRole = canonicalRole(user);
  if (roles.some((role) => canonicalRole(role) === userRole)) return true;
  return isAuditeeRole(user) && roles.some((role) => AUDITEE_ROLES.includes(role));
}

function expandLegacyAuditee(roles = []) {
  return roles.includes(ROLES.AUDITEE) && !roles.includes(ROLES.CUSTOMER_LEGACY)
    ? [...roles, ROLES.CUSTOMER_LEGACY]
    : roles;
}

export function rolesForResource(resource, action = 'view', includeLegacy = true) {
  const actions = ACCESS_MATRIX[resource] || {};
  const roles = Object.prototype.hasOwnProperty.call(actions, action)
    ? actions[action]
    : (action === 'view' ? actions.view || [] : []);
  const unique = Array.from(new Set(roles));
  return includeLegacy ? expandLegacyAuditee(unique) : unique;
}

export function canAccessResource(user, resource, action = 'view') {
  if (!user || !resource) return false;
  return hasRole(user, rolesForResource(resource, action));
}
